import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL_WITH_API } from '../config/api';
import AdminHeader from '../components/AdminHeader';
import ImpersonationBanner from '../components/ImpersonationBanner';
import SideMenuCard, { SideMenuItem } from '../components/SideMenuCard';
import { getExtensionId } from '../config/extension';
import { isStagingOrLocalhost } from '../utils/environment';
import { getChromeRuntime } from '../utils/extensionMessaging';
import { GROUPS_FEATURE_ENABLED } from '../config/features';
import { clearAllAuthStorage, getEffectiveToken, getEffectiveUser } from '../utils/authSession';
import { fetchWithAuthRetry } from '../utils/silentAuthRefresh';
import { MENU_ICON_PATHS } from '../components/menuIcons';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import FeedbackFab from '../components/FeedbackFab';
import {
  exportDashboardSummaryToPdf,
  formatDashboardPdfDate,
  joinDashboardFilterValue
} from '../utils/dashboardPdf';

interface AiUsageResult {
  id: string;
  email: string;
  teacher: string;
  teacherEmail: string;
  group?: string;
  googleClassroom: string;
  courseId: string;
  post: string;
  postTitle: string;
  postType: string;
  aiUsageColor: string;
  color: string;
  date: string;
  updatedAt: string;
}

interface TeacherOption {
  email: string;
  name: string;
}

interface ClassroomOption {
  courseId: string;
  courseName: string;
}

interface GroupOption {
  id: string;
  name: string;
}

// Google Classroom identifiers are opaque single tokens (numeric API ids, or the
// base64-ish segment used in /w/<id> URLs). Real course names almost always contain
// whitespace, so only treat whitespace-free tokens as identifiers.
const isLikelyCourseId = (value?: string, courseId?: string) => {
  const text = (value || '').trim();
  if (!text) return true;
  if (courseId && text === String(courseId).trim()) return true;
  if (text === 'Classroom' || text === 'Classwork') return true;
  if (text.includes('>')) return true;
  if (/\s/.test(text)) return false;
  if (/^\d{8,}$/.test(text)) return true;
  return text.length >= 16
    && /^[A-Za-z0-9+/=_-]+$/.test(text)
    && /\d/.test(text)
    && /[a-z]/.test(text)
    && /[A-Z]/.test(text);
};

const getSafeClassroomLabel = (name?: string, courseId?: string) => {
  if (isLikelyCourseId(name, courseId)) return 'Classroom name unavailable';
  return (name || '').trim();
};

const FILTER_OPTIONS_DEBOUNCE_MS = 300;
const GET_DATA_COOLDOWN_MS = 700;
const RESULTS_REQUEST_TIMEOUT_MS = 12000;
const RESULTS_LOADING_WATCHDOG_MS = 15000;
const RESULTS_RETRY_BACKOFF_MS = 500;
const RESULTS_TIMEOUT_MESSAGE = 'Request timed out. Please try again.';

const normalizeFilterValues = (values: string[] = []) => {
  const seen = new Set<string>();
  const normalized: string[] = [];
  values.forEach((value) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (seen.has(trimmed)) return;
    seen.add(trimmed);
    normalized.push(trimmed);
  });
  return normalized;
};

const toCanonicalFilterValues = (values: string[] = []) => (
  normalizeFilterValues(values).sort((left, right) => left.localeCompare(right))
);

const normalizeSelectionForQuery = (
  selectedValues: string[] = [],
  availableValues: string[] = []
) => {
  const normalizedSelected = toCanonicalFilterValues(selectedValues);
  if (normalizedSelected.length === 0) return [];

  const normalizedAvailable = toCanonicalFilterValues(availableValues);
  if (normalizedAvailable.length === 0) return normalizedSelected;

  const availableSet = new Set(normalizedAvailable);
  const selectedInAvailable = normalizedSelected.filter((value) => availableSet.has(value));
  if (selectedInAvailable.length === 0) return [];

  const selectedSet = new Set(selectedInAvailable);
  const allAvailableSelected = normalizedAvailable.every((value) => selectedSet.has(value));
  return allAvailableSelected ? [] : selectedInAvailable;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const areResultsEquivalent = (previous: AiUsageResult[], incoming: AiUsageResult[]) => {
  if (previous === incoming) return true;
  if (previous.length !== incoming.length) return false;
  for (let index = 0; index < previous.length; index += 1) {
    const prev = previous[index];
    const next = incoming[index];
    if (
      prev.id !== next.id
      || prev.updatedAt !== next.updatedAt
      || prev.date !== next.date
      || prev.color !== next.color
    ) {
      return false;
    }
  }
  return true;
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState<AiUsageResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSuperAdminUser, setIsSuperAdminUser] = useState(false);
  const [roleInitialized, setRoleInitialized] = useState(false);
  const [showAdminDefaultUsageText, setShowAdminDefaultUsageText] = useState(true);
  const [maxRecordsCap, setMaxRecordsCap] = useState<number | null>(null);
  const usePersistentSidebar = isStagingOrLocalhost();
  const adminDefaultLoadTriggeredRef = useRef(false);
  const adminDefaultDateRangeRef = useRef<{ dateFrom: string; dateTo: string }>({ dateFrom: '', dateTo: '' });
  const resultsAbortRef = useRef<AbortController | null>(null);
  const filterOptionsAbortRef = useRef<AbortController | null>(null);
  const filterOptionsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingWatchdogTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFilterOptionsRefreshRef = useRef(false);
  const resultsInFlightRef = useRef(false);
  const lastResultsRequestAtRef = useRef(0);
  const latestCompletedResultsQueryKeyRef = useRef('');
  const lastGetDataClickAtRef = useRef(0);
  const getDataClickSequenceRef = useRef(0);
  const resultsRequestSequenceRef = useRef(0);
  const latestResultsRequestIdRef = useRef(0);
  const latestFilterOptionsRequestIdRef = useRef(0);
  
  // Filter state
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [selectedTeacherEmails, setSelectedTeacherEmails] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedClassroomCourseIds, setSelectedClassroomCourseIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [domainOptions, setDomainOptions] = useState<string[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<TeacherOption[]>([]);
  const [classroomOptions, setClassroomOptions] = useState<ClassroomOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    // Load from localStorage or default to 20
    const saved = localStorage.getItem('adminRowsPerPage');
    return saved ? parseInt(saved, 10) : 20;
  });

  // Sorting state
  type SortColumn = 'email' | 'teacher' | 'group' | 'googleClassroom' | 'postType' | 'aiUsageColor' | 'date';
  const [sortColumn, setSortColumn] = useState<SortColumn>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc'); // Default: newest first

  // Pie chart state
  const [selectedPieColor, setSelectedPieColor] = useState<string | null>(null);
  const [showNoColor, setShowNoColor] = useState(false);
  const [totalMatching, setTotalMatching] = useState<number | null>(null);
  const [serverDistribution, setServerDistribution] = useState<Array<{ color: string; count: number; percentage: number }> | null>(null);
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfSubtitle, setPdfSubtitle] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);
  const appliedFiltersRef = useRef({
    domains: [] as string[],
    groupIds: [] as string[],
    teacherEmails: [] as string[],
    classroomCourseIds: [] as string[],
    dateFrom: '',
    dateTo: '',
    includeNoColor: false
  });
  const domainOptionValues = useMemo(() => toCanonicalFilterValues(domainOptions), [domainOptions]);
  const groupOptionValues = useMemo(
    () => toCanonicalFilterValues(['none', ...groupOptions.map((group) => group.id)]),
    [groupOptions]
  );
  const teacherOptionValues = useMemo(
    () => toCanonicalFilterValues(teacherOptions.map((teacher) => teacher.email)),
    [teacherOptions]
  );
  const classroomOptionValues = useMemo(
    () => toCanonicalFilterValues(classroomOptions.map((classroom) => classroom.courseId)),
    [classroomOptions]
  );

  const dateFormatter = useMemo(() => new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }), []);

  const logResultsTrace = useCallback((event: string, payload: Record<string, unknown> = {}) => {
    if (!usePersistentSidebar) return;
    console.log('[ADMIN_RESULTS_TRACE]', { event, ...payload });
  }, [usePersistentSidebar]);

  useEffect(() => {
    document.title = 'Admin Portal - Traffic Lite';
    try {
      const user = getEffectiveUser();
      if (user) {
        setIsSuperAdminUser(Boolean(user?.isSuperAdmin));
      }
    } catch (parseError) {
      console.warn('Could not parse trafficLiteUser from localStorage:', parseError);
    } finally {
      setRoleInitialized(true);
    }
    // Runtime marker to confirm deployed build metadata
    console.log('[STAGING BUILD]', {
      build: 'timezone-2026-02-02',
      timezoneParamEnabled: true
    });
  }, []);

  const fetchFilterOptions = useCallback(async ({
    teacherEmails = [],
    domains = [],
    groupIds = []
  }: {
    teacherEmails?: string[];
    domains?: string[];
    groupIds?: string[];
  } = {}) => {
    const effectiveToken = getEffectiveToken();
    if (!effectiveToken) return;

    const requestId = ++latestFilterOptionsRequestIdRef.current;
    filterOptionsAbortRef.current?.abort();
    const controller = new AbortController();
    filterOptionsAbortRef.current = controller;

    try {
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;

      const params = new URLSearchParams();
      normalizeFilterValues(teacherEmails).forEach((email) => params.append('teacherEmails', email));
      if (isSuperAdminUser) {
        normalizeFilterValues(domains).forEach((domain) => params.append('domains', domain));
      }
      if (GROUPS_FEATURE_ENABLED) {
        normalizeFilterValues(groupIds).forEach((groupId) => params.append('groupIds', groupId));
      }

      const response = await fetchWithAuthRetry(
        `${baseUrl}/ai-usage/admin/filter-options${params.toString() ? `?${params.toString()}` : ''}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          cache: 'no-store',
          signal: controller.signal
        },
        { token: effectiveToken }
      );

      if (requestId !== latestFilterOptionsRequestIdRef.current) return;
      if (!response.ok) return;
      const data = await response.json();
      if (requestId !== latestFilterOptionsRequestIdRef.current) return;
      if (data.success) {
        if (isSuperAdminUser) {
          setDomainOptions(Array.isArray(data.domains) ? data.domains : []);
        }
        setTeacherOptions(Array.isArray(data.teachers) ? data.teachers : []);
        setClassroomOptions(Array.isArray(data.classrooms) ? data.classrooms : []);
        if (GROUPS_FEATURE_ENABLED) {
          setGroupOptions(Array.isArray(data.groups) ? data.groups : []);
        }
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.warn('Could not load admin filter options:', error);
    }
  }, [isSuperAdminUser]);

  const requestFilterOptionsRefresh = useCallback(() => {
    if (resultsInFlightRef.current) {
      pendingFilterOptionsRefreshRef.current = true;
      return;
    }
    pendingFilterOptionsRefreshRef.current = false;
    void fetchFilterOptions({
      teacherEmails: selectedTeacherEmails,
      domains: selectedDomains,
      groupIds: selectedGroupIds
    });
  }, [fetchFilterOptions, selectedTeacherEmails, selectedDomains, selectedGroupIds]);

  useEffect(() => {
    if (filterOptionsDebounceRef.current) {
      clearTimeout(filterOptionsDebounceRef.current);
    }
    if (resultsInFlightRef.current) {
      pendingFilterOptionsRefreshRef.current = true;
      return;
    }
    filterOptionsDebounceRef.current = setTimeout(() => {
      requestFilterOptionsRefresh();
    }, FILTER_OPTIONS_DEBOUNCE_MS);

    return () => {
      if (filterOptionsDebounceRef.current) {
        clearTimeout(filterOptionsDebounceRef.current);
      }
    };
  }, [
    requestFilterOptionsRefresh
  ]);

  useEffect(() => () => {
    if (filterOptionsDebounceRef.current) {
      clearTimeout(filterOptionsDebounceRef.current);
    }
    if (loadingWatchdogTimeoutRef.current) {
      clearTimeout(loadingWatchdogTimeoutRef.current);
    }
    filterOptionsAbortRef.current?.abort();
    resultsAbortRef.current?.abort();
  }, []);

  // Save rowsPerPage to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('adminRowsPerPage', rowsPerPage.toString());
  }, [rowsPerPage]);

  // Reset to page 1 when filters change or new data is fetched
  useEffect(() => {
    setCurrentPage(1);
  }, [results]);

  const buildResultsQuerySnapshot = useCallback((overrides?: { dateFrom?: string; dateTo?: string }) => {
    const normalizedTeachers = normalizeSelectionForQuery(selectedTeacherEmails, teacherOptionValues);
    const normalizedGroups = GROUPS_FEATURE_ENABLED
      ? normalizeSelectionForQuery(selectedGroupIds, groupOptionValues)
      : [];
    const normalizedCourseIds = normalizeSelectionForQuery(selectedClassroomCourseIds, classroomOptionValues);
    const normalizedDomains = isSuperAdminUser
      ? normalizeSelectionForQuery(selectedDomains, domainOptionValues)
      : [];
    const effectiveDateFrom = overrides?.dateFrom ?? dateFrom;
    const effectiveDateTo = overrides?.dateTo ?? dateTo;
    const key = JSON.stringify({
      teachers: normalizedTeachers,
      groups: normalizedGroups,
      courses: normalizedCourseIds,
      domains: normalizedDomains,
      dateFrom: effectiveDateFrom || null,
      dateTo: effectiveDateTo || null,
      includeNoColor: showNoColor
    });
    return {
      normalizedTeachers,
      normalizedGroups,
      normalizedCourseIds,
      normalizedDomains,
      effectiveDateFrom,
      effectiveDateTo,
      key
    };
  }, [
    dateFrom,
    dateTo,
    isSuperAdminUser,
    selectedTeacherEmails,
    selectedGroupIds,
    selectedClassroomCourseIds,
    selectedDomains,
    teacherOptionValues,
    groupOptionValues,
    classroomOptionValues,
    domainOptionValues,
    showNoColor
  ]);

  const fetchResults = useCallback(async (
    overrides?: { dateFrom?: string; dateTo?: string },
    options?: { force?: boolean; source?: string; clickId?: number }
  ) => {
    const effectiveToken = getEffectiveToken();
    const snapshot = buildResultsQuerySnapshot(overrides);
    const { effectiveDateFrom, effectiveDateTo, key } = snapshot;
    const source = options?.source || 'unknown';
    const clickId = typeof options?.clickId === 'number' ? options.clickId : null;

    if (!effectiveToken) {
      setError('Not authenticated. Please sign in again.');
      setLoading(false);
      logResultsTrace('blocked_no_token', { source, clickId });
      return;
    }

    const now = Date.now();
    if (!options?.force) {
      if (resultsInFlightRef.current) {
        logResultsTrace('ignored_in_flight', { source, clickId });
        return;
      }
      if (latestCompletedResultsQueryKeyRef.current === key) {
        logResultsTrace('ignored_duplicate_signature', { source, clickId });
        return;
      }
      if (now - lastResultsRequestAtRef.current < GET_DATA_COOLDOWN_MS) {
        logResultsTrace('ignored_cooldown', { source, clickId });
        return;
      }
    }

    const requestId = ++latestResultsRequestIdRef.current;
    const requestSequence = ++resultsRequestSequenceRef.current;
    resultsInFlightRef.current = true;
    lastResultsRequestAtRef.current = now;
    logResultsTrace('request_start', {
      source,
      clickId,
      requestId: requestSequence,
      force: Boolean(options?.force),
      keyLength: key.length
    });

    setLoading(true);
    setError(null);
    if (loadingWatchdogTimeoutRef.current) {
      clearTimeout(loadingWatchdogTimeoutRef.current);
    }
    loadingWatchdogTimeoutRef.current = setTimeout(() => {
      if (!resultsInFlightRef.current) return;
      if (requestId !== latestResultsRequestIdRef.current) return;
      logResultsTrace('watchdog_unlock', { source, clickId, requestId: requestSequence });
      resultsAbortRef.current?.abort();
      resultsInFlightRef.current = false;
      setLoading(false);
      setError((previous) => previous || RESULTS_TIMEOUT_MESSAGE);
    }, RESULTS_LOADING_WATCHDOG_MS);

    let terminalState:
      | 'success'
      | 'http_error'
      | 'invalid_content_type'
      | 'invalid_payload'
      | 'aborted'
      | 'exception'
      | 'stale'
      | 'timeout'
      | 'retrying_timeout'
      | 'retrying_network' = 'success';

    try {
      const params = new URLSearchParams();
      snapshot.normalizedTeachers.forEach((email) => params.append('teacherEmails', email));
      snapshot.normalizedGroups.forEach((groupId) => params.append('groupIds', groupId));
      snapshot.normalizedCourseIds.forEach((courseId) => params.append('courseIds', courseId));
      snapshot.normalizedDomains.forEach((domain) => params.append('domains', domain));
      if (effectiveDateFrom) params.append('dateFrom', effectiveDateFrom);
      if (effectiveDateTo) params.append('dateTo', effectiveDateTo);
      params.append('includeNoColor', showNoColor ? 'true' : 'false');
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone) {
          params.append('timezone', timezone);
        }
      } catch (_) {
        // ignore timezone lookup errors
      }

      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const endpoint = '/ai-usage/admin/results';
      params.append('t', Date.now().toString());

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const url = `${baseUrl}${endpoint}${queryString}`;

      for (let attempt = 1; attempt <= 2; attempt += 1) {
        let didTimeout = false;
        const controller = new AbortController();
        resultsAbortRef.current = controller;
        const timeoutHandle = setTimeout(() => {
          didTimeout = true;
          controller.abort();
        }, RESULTS_REQUEST_TIMEOUT_MS);

        try {
          const response = await fetchWithAuthRetry(
            url,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              },
              cache: 'no-store',
              signal: controller.signal
            },
            { token: effectiveToken }
          );
          clearTimeout(timeoutHandle);

          if (requestId !== latestResultsRequestIdRef.current) {
            terminalState = 'stale';
            return;
          }

          const contentType = response.headers.get('content-type') || '';

          if (!response.ok) {
            terminalState = 'http_error';
            let errorMessage = '';
            try {
              const errorData = await response.json();
              errorMessage = errorData.error || `Server error: ${response.status}`;
            } catch {
              errorMessage = `Server error: ${response.status}`;
            }

            if (response.status === 401) {
              setError(`Authentication failed (401). Please sign out and sign in again. ${errorMessage}`);
            } else if (response.status === 403) {
              setError(`Access denied (403). Admin privileges required. ${errorMessage}`);
            } else {
              setError(`Server error (${response.status}): ${errorMessage}`);
            }
            return;
          }

          if (!contentType.includes('application/json')) {
            terminalState = 'invalid_content_type';
            setError('Server returned unexpected response format. Please refresh and try again.');
            return;
          }

          const data = await response.json();
          if (requestId !== latestResultsRequestIdRef.current) {
            terminalState = 'stale';
            return;
          }
          if (data.success && data.results) {
            const parsedMaxRecords = Number(data.maxRecords);
            if (Number.isFinite(parsedMaxRecords) && parsedMaxRecords > 0) {
              setMaxRecordsCap(parsedMaxRecords);
            } else if (isSuperAdminUser) {
              setMaxRecordsCap(50000);
            }
            const parsedTotal = Number(data.totalMatching);
            setTotalMatching(Number.isFinite(parsedTotal) ? parsedTotal : null);
            setServerDistribution(Array.isArray(data.distribution) ? data.distribution : null);
            latestCompletedResultsQueryKeyRef.current = key;
            appliedFiltersRef.current = {
              domains: snapshot.normalizedDomains,
              groupIds: snapshot.normalizedGroups,
              teacherEmails: snapshot.normalizedTeachers,
              classroomCourseIds: snapshot.normalizedCourseIds,
              dateFrom: effectiveDateFrom || '',
              dateTo: effectiveDateTo || '',
              includeNoColor: showNoColor
            };
            setResults((previous) => (areResultsEquivalent(previous, data.results) ? previous : data.results));
            terminalState = 'success';
            return;
          }
          terminalState = 'invalid_payload';
          setError('Invalid response from server');
          return;
        } catch (err) {
          clearTimeout(timeoutHandle);
          if (didTimeout) {
            if (attempt < 2) {
              terminalState = 'retrying_timeout';
              logResultsTrace('request_retry', { source, clickId, requestId: requestSequence, reason: 'timeout' });
              await sleep(RESULTS_RETRY_BACKOFF_MS);
              continue;
            }
            terminalState = 'timeout';
            setError(RESULTS_TIMEOUT_MESSAGE);
            return;
          }
          if (controller.signal.aborted) {
            terminalState = 'aborted';
            return;
          }
          if (attempt < 2) {
            terminalState = 'retrying_network';
            logResultsTrace('request_retry', { source, clickId, requestId: requestSequence, reason: 'network' });
            await sleep(RESULTS_RETRY_BACKOFF_MS);
            continue;
          }
          terminalState = 'exception';
          console.error('Error fetching admin results:', err);
          setError('Failed to fetch results. Please try again.');
          return;
        }
      }
    } finally {
      if (loadingWatchdogTimeoutRef.current) {
        clearTimeout(loadingWatchdogTimeoutRef.current);
      }
      resultsInFlightRef.current = false;
      if (requestId === latestResultsRequestIdRef.current) {
        setLoading(false);
      }
      if (pendingFilterOptionsRefreshRef.current) {
        requestFilterOptionsRefresh();
      }
      logResultsTrace('request_end', {
        source,
        clickId,
        requestId: requestSequence,
        terminalState
      });
    }
  }, [
    buildResultsQuerySnapshot,
    isSuperAdminUser,
    logResultsTrace,
    requestFilterOptionsRefresh,
    showNoColor
  ]);

  const fetchResultsRef = useRef(fetchResults);
  fetchResultsRef.current = fetchResults;
  const showNoColorDidMountRef = useRef(false);
  useEffect(() => {
    if (!showNoColorDidMountRef.current) {
      showNoColorDidMountRef.current = true;
      return;
    }
    if (!latestCompletedResultsQueryKeyRef.current) return;
    void fetchResultsRef.current(undefined, { force: true, source: 'show-no-color' });
  }, [showNoColor]);

  const hasAdminFiltersChangedFromDefault = useCallback(() => {
    if (selectedDomains.length > 0 || selectedTeacherEmails.length > 0 || selectedGroupIds.length > 0 || selectedClassroomCourseIds.length > 0) {
      return true;
    }
    const { dateFrom: defaultFrom, dateTo: defaultTo } = adminDefaultDateRangeRef.current;
    if (!defaultFrom || !defaultTo) return false;
    return dateFrom !== defaultFrom || dateTo !== defaultTo;
  }, [
    selectedDomains,
    selectedTeacherEmails,
    selectedGroupIds,
    selectedClassroomCourseIds,
    dateFrom,
    dateTo
  ]);

  const handleGetDataClick = useCallback(() => {
    const clickId = ++getDataClickSequenceRef.current;
    const now = Date.now();
    if (now - lastGetDataClickAtRef.current < GET_DATA_COOLDOWN_MS) {
      logResultsTrace('ignored_click_cooldown', { clickId });
      return;
    }
    lastGetDataClickAtRef.current = now;
    logResultsTrace('click_get_data', { clickId });
    if (!isSuperAdminUser && hasAdminFiltersChangedFromDefault()) {
      setShowAdminDefaultUsageText(false);
    }
    void fetchResults(undefined, { source: 'button', clickId });
  }, [fetchResults, hasAdminFiltersChangedFromDefault, isSuperAdminUser, logResultsTrace]);

  useEffect(() => {
    if (!roleInitialized || isSuperAdminUser || adminDefaultLoadTriggeredRef.current) return;
    const now = new Date();
    const dateToValue = now.toISOString().slice(0, 10);
    const fromDate = new Date(now);
    fromDate.setDate(fromDate.getDate() - 30);
    const dateFromValue = fromDate.toISOString().slice(0, 10);
    setDateFrom(dateFromValue);
    setDateTo(dateToValue);
    setShowAdminDefaultUsageText(true);
    adminDefaultDateRangeRef.current = { dateFrom: dateFromValue, dateTo: dateToValue };
    adminDefaultLoadTriggeredRef.current = true;
    void fetchResults(
      { dateFrom: dateFromValue, dateTo: dateToValue },
      { force: true, source: 'admin-default-load' }
    );
  }, [fetchResults, roleInitialized, isSuperAdminUser]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return dateFormatter.format(date);
    } catch (error) {
      console.warn('Error formatting date:', error);
      return dateString;
    }
  };

  const getColorBadgeClass = (color: string) => {
    switch (color.toLowerCase()) {
      case 'red':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'orange':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'green':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'blue':
        return 'bg-blue-100 text-blue-900 border-blue-300';
      case 'na':
        return 'bg-gray-400 text-gray-900 border-gray-600';
      case 'none':
        return 'bg-white text-gray-900 border-gray-900';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const getNormalizedColor = (value?: string | null) => (value || 'none').toLowerCase();
  const formatDashboardColorLabel = (color: string) => {
    if (color === 'none') return 'No Selection';
    if (color === 'na') return 'N/A';
    return color.charAt(0).toUpperCase() + color.slice(1);
  };

  const domainDropdownOptions = domainOptions.map((domain) => ({ value: domain, label: domain }));
  const groupDropdownOptions = [
    { value: 'none', label: 'No Group' },
    ...groupOptions.map((group) => ({ value: group.id, label: group.name }))
  ];
  const teacherDropdownOptions = teacherOptions.map((teacher) => ({
    value: teacher.email,
    label: teacher.name
  }));
  const classroomDropdownOptions = classroomOptions.map((classroom) => ({
    value: classroom.courseId,
    label: getSafeClassroomLabel(classroom.courseName, classroom.courseId)
  }));

  const hasSingleSuperAdminDomain = selectedDomains.length === 1;
  const hasMultipleSuperAdminDomains = selectedDomains.length > 1;
  const hasMultiGroupSelection = selectedGroupIds.length > 1;
  const hasMultiTeacherSelection = selectedTeacherEmails.length > 1;

  const groupDisabled = isSuperAdminUser
    ? (!hasSingleSuperAdminDomain || hasMultiTeacherSelection)
    : hasMultiTeacherSelection;
  const teacherDisabled = isSuperAdminUser
    ? (!hasSingleSuperAdminDomain || hasMultiGroupSelection)
    : hasMultiGroupSelection;
  const classroomDisabled = hasMultiGroupSelection
    || hasMultipleSuperAdminDomains
    || selectedTeacherEmails.length === 0;

  useEffect(() => {
    if (!isSuperAdminUser) return;
    if (!hasMultipleSuperAdminDomains) return;
    if (selectedGroupIds.length === 0 && selectedTeacherEmails.length === 0 && selectedClassroomCourseIds.length === 0) return;
    setSelectedGroupIds([]);
    setSelectedTeacherEmails([]);
    setSelectedClassroomCourseIds([]);
  }, [
    isSuperAdminUser,
    hasMultipleSuperAdminDomains,
    selectedGroupIds,
    selectedTeacherEmails,
    selectedClassroomCourseIds
  ]);

  useEffect(() => {
    if (!hasMultiGroupSelection) return;
    if (selectedTeacherEmails.length === 0 && selectedClassroomCourseIds.length === 0) return;
    setSelectedTeacherEmails([]);
    setSelectedClassroomCourseIds([]);
  }, [hasMultiGroupSelection, selectedTeacherEmails, selectedClassroomCourseIds]);

  useEffect(() => {
    if (!hasMultiTeacherSelection) return;
    if (selectedGroupIds.length === 0) return;
    setSelectedGroupIds([]);
  }, [hasMultiTeacherSelection, selectedGroupIds]);

  useEffect(() => {
    if (!classroomDisabled) return;
    if (selectedClassroomCourseIds.length === 0) return;
    setSelectedClassroomCourseIds([]);
  }, [classroomDisabled, selectedClassroomCourseIds]);

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) {
      return;
    }

    clearAllAuthStorage();

    const runtime = getChromeRuntime();
    if (runtime) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) {
          runtime.sendMessage(extensionId, message, () => {});
        } else {
          runtime.sendMessage(message, () => {});
        }
      } catch (error) {
        console.error('Sign-out storage clear failed:', error);
      }
    }

    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  const sidebarItems: SideMenuItem[] = [
    ...(isSuperAdminUser
      ? [
          { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), icon: MENU_ICON_PATHS.users },
          { key: 'domains', label: 'Domains', onClick: () => navigate('/admin/domains'), icon: MENU_ICON_PATHS.domains },
          { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), isActive: true, icon: MENU_ICON_PATHS.dataDashboard },
          { key: 'policy', label: 'Policy Templates', onClick: () => navigate('/admin/settings'), icon: MENU_ICON_PATHS.policyDetails },
          { key: 'aiAssist', label: 'GPS', onClick: () => navigate('/admin/ai-assist'), icon: MENU_ICON_PATHS.aiAssist }
        ]
      : [
          { key: 'policy', label: 'Policy Details', onClick: () => navigate('/admin/settings'), icon: MENU_ICON_PATHS.policyDetails },
          { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), icon: MENU_ICON_PATHS.users },
          { key: 'groups', label: 'Groups', onClick: () => navigate('/admin/groups'), icon: MENU_ICON_PATHS.groups },
          { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), isActive: true, icon: MENU_ICON_PATHS.dataDashboard }
        ]),
    ...(isSuperAdminUser
      ? [
          { key: 'feedback', label: 'Feedback', onClick: () => navigate('/admin/feedback'), icon: MENU_ICON_PATHS.feedback },
          { key: 'signout', label: 'Sign Out', onClick: handleSignOut, isDanger: true, icon: MENU_ICON_PATHS.signOut }
        ]
      : [])
  ];

  const handleDomainSelect = (domains: string[]) => {
    setSelectedDomains(domains);
    setSelectedGroupIds([]);
    setSelectedTeacherEmails([]);
    setSelectedClassroomCourseIds([]);
    setCurrentPage(1);
  };

  const handleTeacherSelect = (emails: string[]) => {
    setSelectedTeacherEmails(emails);
    setSelectedClassroomCourseIds([]);
    if (emails.length > 1) {
      setSelectedGroupIds([]);
    }
    setCurrentPage(1);
  };

  const handleGroupSelect = (groupIds: string[]) => {
    setSelectedGroupIds(groupIds);
    setSelectedTeacherEmails([]);
    setSelectedClassroomCourseIds([]);
    setCurrentPage(1);
  };

  const handleClassroomSelect = (courseIds: string[]) => {
    setSelectedClassroomCourseIds(courseIds);
    setCurrentPage(1);
  };

  // Sorting logic
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: default to ascending (except date defaults to desc)
      setSortColumn(column);
      setSortDirection(column === 'date' ? 'desc' : 'asc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const usesServerDataset = serverDistribution != null;
  const filteredResults = useMemo(() => (
    usesServerDataset || showNoColor
      ? results
      : results.filter((result) => getNormalizedColor(result.color) !== 'none')
  ), [results, showNoColor, usesServerDataset]);

  const colorDistribution = useMemo(() => {
    if (serverDistribution) return serverDistribution;
    const counts: Record<string, number> = {};
    filteredResults.forEach((result) => {
      const color = getNormalizedColor(result.color);
      counts[color] = (counts[color] || 0) + 1;
    });
    const total = filteredResults.length;
    const rows = Object.entries(counts)
      .filter(([, count]) => count > 0)
      .map(([color, count]) => {
        const exact = total > 0 ? (count / total) * 100 : 0;
        return { color, count, percentage: Math.floor(exact), remainder: exact - Math.floor(exact) };
      });
    let leftover = 100 - rows.reduce((sum, row) => sum + row.percentage, 0);
    if (total === 0) leftover = 0;
    const byRemainder = [...rows].sort((a, b) => b.remainder - a.remainder || b.count - a.count);
    for (let index = 0; index < byRemainder.length && leftover > 0; index += 1) {
      byRemainder[index].percentage += 1;
      leftover -= 1;
    }
    return rows
      .map(({ color, count, percentage }) => ({ color, count, percentage }))
      .sort((a, b) => b.count - a.count);
  }, [filteredResults, serverDistribution]);

  useEffect(() => {
    if (!showNoColor && selectedPieColor === 'none') {
      setSelectedPieColor(null);
    }
  }, [showNoColor, selectedPieColor]);

  // Handle pie slice click
  const handlePieSliceClick = (color: string) => {
    if (selectedPieColor === color) {
      // Toggle off
      setSelectedPieColor(null);
    } else {
      // Select this color
      setSelectedPieColor(color);
    }
    setCurrentPage(1); // Reset to first page
  };

  const finalSortedResults = useMemo(() => {
    const sortedResults = [...filteredResults].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortColumn) {
        case 'email':
          aVal = a.email || a.teacherEmail || '';
          bVal = b.email || b.teacherEmail || '';
          break;
        case 'teacher':
          aVal = a.teacher || a.teacherEmail || '';
          bVal = b.teacher || b.teacherEmail || '';
          break;
        case 'group':
          aVal = a.group || 'No Group';
          bVal = b.group || 'No Group';
          break;
        case 'googleClassroom':
          aVal = a.googleClassroom || '';
          bVal = b.googleClassroom || '';
          break;
        case 'postType':
          aVal = a.postType || '';
          bVal = b.postType || '';
          break;
        case 'aiUsageColor':
          aVal = a.aiUsageColor || '';
          bVal = b.aiUsageColor || '';
          break;
        case 'date':
          aVal = new Date(a.date).getTime();
          bVal = new Date(b.date).getTime();
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });

    if (!selectedPieColor) {
      return sortedResults;
    }

    const selectedColorResults = sortedResults.filter((result) => getNormalizedColor(result.color) === selectedPieColor);
    const otherResults = sortedResults.filter((result) => getNormalizedColor(result.color) !== selectedPieColor);
    return [...selectedColorResults, ...otherResults];
  }, [filteredResults, selectedPieColor, sortColumn, sortDirection]);

  const validRowsPerPage = typeof rowsPerPage === 'number' && rowsPerPage > 0 ? rowsPerPage : 20;
  const { totalPages, startIndex, paginatedResults, showPagination } = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(finalSortedResults.length / validRowsPerPage));
    const startIndex = (currentPage - 1) * validRowsPerPage;
    const endIndex = startIndex + validRowsPerPage;
    return {
      totalPages,
      startIndex,
      paginatedResults: finalSortedResults.slice(startIndex, endIndex),
      showPagination: finalSortedResults.length > validRowsPerPage
    };
  }, [currentPage, finalSortedResults, validRowsPerPage]);

  const resolvedMaxRecordsCap = useMemo(() => {
    if (Number.isFinite(Number(maxRecordsCap)) && Number(maxRecordsCap) > 0) {
      return Number(maxRecordsCap);
    }
    if (isSuperAdminUser) return 50000;
    return null;
  }, [isSuperAdminUser, maxRecordsCap]);
  const retrievedCount = finalSortedResults.length;
  const matchingTotal = totalMatching == null ? retrievedCount : totalMatching;
  const tableIsPartial = totalMatching != null && retrievedCount < matchingTotal;

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    const numValue = typeof newRowsPerPage === 'string' ? parseInt(newRowsPerPage, 10) : newRowsPerPage;
    if (!isNaN(numValue) && numValue > 0) {
      setRowsPerPage(numValue);
      setCurrentPage(1); // Reset to first page
      console.log('🔍 Admin: Rows per page changed to', numValue);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    return (
      <span className="traffic-sort-indicator" style={{ marginLeft: '6px', display: 'inline-flex', alignItems: 'center' }}>
        <img
          src={sortDirection === 'asc' ? MENU_ICON_PATHS.upArrow : MENU_ICON_PATHS.downArrow}
          alt=""
          style={{ width: '11px', height: '11px', objectFit: 'contain' }}
        />
      </span>
    );
  };

  // Render pie chart
  const renderPieChart = () => {
    if (colorDistribution.length === 0) {
      return (
        <div style={{
          width: '200px',
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '14px',
          textAlign: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#fafafa'
        }}>
          No data for selected filters
        </div>
      );
    }

    const size = 320;
    const radius = 130;
    const centerX = size / 2;
    const centerY = size / 2;

    // Color mapping
    const colorMap: Record<string, string> = {
      red: '#ef4444',
      orange: '#f59e0b',
      // Brighter lemon yellow so it reads clearly vs. orange in the pie + legend
      yellow: '#fde047',
      green: '#22c55e',
      blue: '#2563eb',
      na: '#9ca3af',
      none: '#ffffff'
    };

    // Calculate pie slices
    let currentAngle = -90; // Start at top
    const isSingleFullSlice = colorDistribution.length === 1 && colorDistribution[0].percentage === 100;
    if (isSingleFullSlice) {
      const onlySlice = colorDistribution[0];
      const fill = colorMap[onlySlice.color] || '#ccc';
      const isSelected = selectedPieColor === onlySlice.color;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
          <svg width={size} height={size} style={{ cursor: 'pointer' }}>
            <circle
              cx={centerX}
              cy={centerY}
              r={radius}
              fill={fill}
              stroke={onlySlice.color === 'none' ? '#111827' : (isSelected ? '#333' : 'white')}
              strokeWidth={onlySlice.color === 'none' || isSelected ? 3 : 2}
              opacity={isSelected ? 1 : 0.9}
              onClick={() => handlePieSliceClick(onlySlice.color)}
              style={{ cursor: 'pointer', transition: 'opacity 0.2s, stroke-width 0.2s' }}
            />
          </svg>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
            <div
              onClick={() => handlePieSliceClick(onlySlice.color)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: isSelected ? '#f0f0f0' : 'transparent',
                fontWeight: isSelected ? '600' : '400'
              }}
            >
              <div style={{
                width: '16px',
                height: '16px',
                backgroundColor: fill,
                borderRadius: '3px',
                border: onlySlice.color === 'none' ? '2px solid #111827' : (isSelected ? '2px solid #333' : '1px solid #ddd')
              }} />
              <span style={{ color: '#555' }}>
                {formatDashboardColorLabel(onlySlice.color)}: {onlySlice.percentage}%
              </span>
            </div>
          </div>
        </div>
      );
    }

    const slices = colorDistribution.map(item => {
      const sweepAngle = (item.percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + sweepAngle;
      currentAngle = endAngle;

      // Calculate arc path
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;
      const x1 = centerX + radius * Math.cos(startRad);
      const y1 = centerY + radius * Math.sin(startRad);
      const x2 = centerX + radius * Math.cos(endRad);
      const y2 = centerY + radius * Math.sin(endRad);
      const largeArc = sweepAngle > 180 ? 1 : 0;

      const path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

      return {
        ...item,
        path,
        fill: colorMap[item.color] || '#ccc',
        isSelected: selectedPieColor === item.color
      };
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
        <svg width={size} height={size} style={{ cursor: 'pointer' }}>
          {slices.map((slice, index) => (
            <g key={index}>
              <path
                d={slice.path}
                fill={slice.fill}
                stroke={slice.color === 'none' ? '#111827' : (slice.isSelected ? '#333' : 'white')}
                strokeWidth={slice.color === 'none' || slice.isSelected ? 3 : 2}
                opacity={slice.isSelected ? 1 : 0.9}
                onClick={() => handlePieSliceClick(slice.color)}
                style={{ 
                  cursor: 'pointer',
                  transition: 'opacity 0.2s, stroke-width 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!slice.isSelected) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!slice.isSelected) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
              />
            </g>
          ))}
        </svg>
        
        {/* Legend */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
          {slices.map((slice, index) => (
            <div 
              key={index}
              onClick={() => handlePieSliceClick(slice.color)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: slice.isSelected ? '#f0f0f0' : 'transparent',
                fontWeight: slice.isSelected ? '600' : '400'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = slice.isSelected ? '#f0f0f0' : 'transparent'}
            >
              <div style={{
                width: '16px',
                height: '16px',
                backgroundColor: slice.fill,
                borderRadius: '3px',
                border: slice.color === 'none' ? '2px solid #111827' : (slice.isSelected ? '2px solid #333' : '1px solid #ddd')
              }} />
              <span style={{ color: '#555' }}>
                {formatDashboardColorLabel(slice.color)}: {slice.percentage}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // CSV export (retrieved rows only, not the current page and not unretrieved history)
  const exportToCSV = () => {
    if (finalSortedResults.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = isSuperAdminUser
      ? ['Email', 'Teacher', 'Group', 'Google Classroom', 'Post Type', 'AI-Usage Color', 'Date']
      : ['Teacher', 'Group', 'Google Classroom', 'Post Type', 'AI-Usage Color', 'Date'];
    const rows = finalSortedResults.map(r => (
      isSuperAdminUser
        ? [
            r.email || r.teacherEmail || '-',
            r.teacher || r.teacherEmail,
            r.group || 'No Group',
            r.googleClassroom,
            r.postType || '-',
            r.aiUsageColor,
            formatDate(r.date)
          ]
        : [
            r.teacher || r.teacherEmail,
            r.group || 'No Group',
            r.googleClassroom,
            r.postType || '-',
            r.aiUsageColor,
            formatDate(r.date)
          ]
    ));

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape commas and quotes
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ai-usage-scale-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportLinkStyle = {
    fontSize: '14px',
    color: '#4285f4',
    textDecoration: 'none',
    cursor: 'pointer'
  } as const;

  const handleExportSummaryPdf = async () => {
    const applied = appliedFiltersRef.current;
    const filters = [
      ...(isSuperAdminUser
        ? [{ label: 'Domain(s)', value: joinDashboardFilterValue(applied.domains) }]
        : []),
      ...(GROUPS_FEATURE_ENABLED
        ? [{
            label: 'Group(s)',
            value: joinDashboardFilterValue(applied.groupIds.map((groupId) => (
              groupId === 'none'
                ? 'No Group'
                : (groupOptions.find((group) => group.id === groupId)?.name || groupId)
            )))
          }]
        : []),
      {
        label: 'Teacher(s)',
        value: joinDashboardFilterValue(applied.teacherEmails.map((email) => (
          teacherOptions.find((teacher) => teacher.email === email)?.name || email
        )))
      },
      {
        label: 'Google Classroom(s)',
        value: joinDashboardFilterValue(applied.classroomCourseIds.map((courseId) => {
          const classroom = classroomOptions.find((item) => item.courseId === courseId);
          return classroom
            ? getSafeClassroomLabel(classroom.courseName, classroom.courseId)
            : courseId;
        }))
      },
      {
        label: 'No Color',
        value: applied.includeNoColor ? 'Included' : 'Hidden'
      }
    ];

    setExportingPdf(true);
    try {
      await exportDashboardSummaryToPdf({
        title: pdfTitle,
        subtitle: pdfSubtitle,
        filters,
        dateFrom: formatDashboardPdfDate(applied.dateFrom),
        dateTo: formatDashboardPdfDate(applied.dateTo),
        slices: colorDistribution.map((slice) => ({
          color: slice.color,
          count: slice.count,
          percentage: slice.percentage
        }))
      });
      setPdfExportOpen(false);
    } catch (exportError) {
      console.error('Dashboard PDF export failed:', exportError);
      window.alert('Failed to export the dashboard summary. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div
        className={usePersistentSidebar ? 'max-w-[1600px] mx-auto flex gap-6 items-start' : 'max-w-[1400px] mx-auto'}
      >
        {usePersistentSidebar && <SideMenuCard title={isSuperAdminUser ? 'Super Admin Menu' : 'Admin Menu'} items={sidebarItems} />}
        <div className={`${usePersistentSidebar ? 'flex-1 min-w-0' : ''} bg-white rounded-lg shadow-md p-8`} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <AdminHeader
          title={isSuperAdminUser ? 'Traffic Lite Super Admin' : 'Traffic Lite Admin'}
          subtitle="Data Dashboard"
          helperText="Use this page to see how teachers are utilizing the AI-usage scale on Google Classroom"
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />
        <ImpersonationBanner />

        {/* Filter Section + Pie Chart */}
        <div style={{ 
          marginBottom: '30px',
          display: 'flex',
          gap: '30px',
          alignItems: 'flex-start'
        }}>
          {/* Filter Box */}
          <div
            className="flex-1 p-5 rounded-lg border"
            style={{
              backgroundColor: '#f8f9fa',
              borderColor: '#dee2e6'
            }}
          >
            <h2
              className="text-lg font-semibold mb-4"
              style={{ color: '#212529' }}
            >
              Filter:
            </h2>
          
          {isSuperAdminUser && (
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px',
              marginBottom: '15px'
            }}>
              <div>
                <MultiSelectDropdown
                  label="Domain:"
                  options={domainDropdownOptions}
                  selectedValues={selectedDomains}
                  onChange={handleDomainSelect}
                  placeholder="All domains"
                  applied={selectedDomains.length > 0}
                />
              </div>
              {GROUPS_FEATURE_ENABLED ? (
                <div>
                  <MultiSelectDropdown
                    label="Group:"
                    options={groupDropdownOptions}
                    selectedValues={selectedGroupIds}
                    onChange={handleGroupSelect}
                    placeholder="All groups"
                    disabled={groupDisabled}
                    applied={selectedGroupIds.length > 0}
                  />
                </div>
              ) : (
                <div />
              )}
            </div>
          )}

          {GROUPS_FEATURE_ENABLED && !isSuperAdminUser && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px',
              marginBottom: '15px'
            }}>
              <div>
                <MultiSelectDropdown
                  label="Group:"
                  options={groupDropdownOptions}
                  selectedValues={selectedGroupIds}
                  onChange={handleGroupSelect}
                  placeholder="All groups"
                  disabled={groupDisabled}
                  applied={selectedGroupIds.length > 0}
                />
              </div>
              <div />
            </div>
          )}

          {/* Row 1: Teacher + Google Classroom */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '15px',
            marginBottom: '15px'
          }}>
            {/* Teacher dropdown */}
            <div>
              <MultiSelectDropdown
                label="Teacher:"
                options={teacherDropdownOptions}
                selectedValues={selectedTeacherEmails}
                onChange={handleTeacherSelect}
                placeholder="All teachers"
                disabled={teacherDisabled}
                applied={selectedTeacherEmails.length > 0}
              />
            </div>
            
            {/* Google Classroom dropdown (dependent on teacher) */}
            <div>
              <MultiSelectDropdown
                label="Google Classroom:"
                options={classroomDropdownOptions}
                selectedValues={selectedClassroomCourseIds}
                onChange={handleClassroomSelect}
                placeholder="All classrooms"
                disabled={classroomDisabled}
                applied={selectedClassroomCourseIds.length > 0}
              />
            </div>
          </div>
          
          {/* Row 2: Date From + Date To */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '15px',
            marginBottom: '15px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#555',
                marginBottom: '5px'
              }}>
                Date From:
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#555',
                marginBottom: '5px'
              }}>
                Date To:
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={handleGetDataClick}
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: '#4285f4',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Loading...' : 'Get Data'}
            </button>
          </div>
          {!isSuperAdminUser && showAdminDefaultUsageText && (
            <div style={{ fontSize: '14px', color: '#4b5563', marginTop: '10px', marginBottom: '2px' }}>
              Showing all usage for the last 30 days.
            </div>
          )}
          </div>

          {/* Pie Chart */}
          {results.length > 0 && (
            <div
              className="rounded-lg border p-6"
              style={{
                backgroundColor: '#f8f9fa',
                borderColor: '#dee2e6',
                minWidth: '400px',
                flex: '0 0 auto'
              }}
            >
              <h2
                className="text-lg font-semibold mb-5 text-center"
                style={{ color: '#212529' }}
              >
                AI-Usage Distribution
              </h2>
              {renderPieChart()}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c00',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* Results Table */}
        {results.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            {/* Header with results count and export */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <div style={{ fontSize: '16px', color: '#555' }}>
                {tableIsPartial ? (
                  <>
                    Showing <strong>{retrievedCount.toLocaleString()}</strong> of <strong>{matchingTotal.toLocaleString()}</strong> matching records.
                    {resolvedMaxRecordsCap ? (
                      <div style={{ marginTop: '4px', fontSize: '14px', color: '#555' }}>
                        ({resolvedMaxRecordsCap.toLocaleString()} max)
                      </div>
                    ) : null}
                    <div style={{ marginTop: '4px', fontSize: '14px', color: '#555' }}>
                      The table and CSV include the newest {retrievedCount.toLocaleString()} retrieved records.
                      {resolvedMaxRecordsCap ? ` Retrieval stops at ${resolvedMaxRecordsCap.toLocaleString()}.` : ''}
                      {' '}Older matches stay stored. The chart and PDF count every match.
                    </div>
                  </>
                ) : (
                  <>
                    There are <strong>{matchingTotal.toLocaleString()}</strong> posts matching your criteria.
                    {resolvedMaxRecordsCap ? (
                      <div style={{ marginTop: '4px', fontSize: '14px', color: '#555' }}>
                        ({resolvedMaxRecordsCap.toLocaleString()} max)
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '18px' }}>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showNoColor}
                    onChange={(e) => setShowNoColor(e.target.checked)}
                    style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                  />
                  Show No Selection
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      exportToCSV();
                    }}
                    style={exportLinkStyle}
                  >
                    Export results to csv
                  </a>
                  {tableIsPartial && (
                    <span style={{ fontSize: '12px', color: '#6b7280', maxWidth: '220px', textAlign: 'right' }}>
                      CSV is the {retrievedCount.toLocaleString()} retrieved rows, not all {matchingTotal.toLocaleString()} matches.
                    </span>
                  )}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setPdfExportOpen(true);
                    }}
                    style={exportLinkStyle}
                  >
                    Export summary to PDF
                  </a>
                </div>
              </div>
            </div>

            {/* Rows per page selector */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center',
              marginBottom: '10px',
              gap: '10px'
            }}>
              <label style={{ fontSize: '14px', color: '#555' }}>
                Rows per page:
              </label>
              <select
                value={String(rowsPerPage || 20)}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value, 10);
                  if (!isNaN(newValue) && newValue > 0) {
                    handleRowsPerPageChange(newValue);
                  }
                }}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{
                    backgroundColor: '#212529',
                    borderBottom: '2px solid #212529'
                  }}>
                    {isSuperAdminUser && (
                      <th
                        onClick={() => handleSort('email')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        Email{renderSortIndicator('email')}
                      </th>
                    )}
                    <th
                      onClick={() => handleSort('teacher')}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: 'white',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Teacher{renderSortIndicator('teacher')}
                    </th>
                    <th
                      onClick={() => handleSort('group')}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: 'white',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Group{renderSortIndicator('group')}
                    </th>
                    <th
                      onClick={() => handleSort('googleClassroom')}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: 'white',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Google Classroom{renderSortIndicator('googleClassroom')}
                    </th>
                    <th
                      onClick={() => handleSort('postType')}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: 'white',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Post Type{renderSortIndicator('postType')}
                    </th>
                    <th
                      onClick={() => handleSort('aiUsageColor')}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: 'white',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        width: '160px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      AI-Usage Scale Color{renderSortIndicator('aiUsageColor')}
                    </th>
                    <th
                      onClick={() => handleSort('date')}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: 'white',
                        width: '170px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Date{renderSortIndicator('date')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedResults.map((result, index) => (
                    <tr 
                      key={result.id || (startIndex + index)}
                      style={{
                        borderBottom: '1px solid #eee',
                        backgroundColor: (startIndex + index) % 2 === 0 ? 'white' : '#fafafa'
                      }}
                    >
                      {isSuperAdminUser && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>
                          {result.email || result.teacherEmail || '-'}
                        </td>
                      )}
                      <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>
                        {result.teacher || result.teacherEmail}
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>
                        {result.group || 'No Group'}
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>
                        {(() => {
                          const label = getSafeClassroomLabel(result.googleClassroom, result.courseId);
                          const hasCourseLink = Boolean(result.courseId && result.courseId.trim());
                          if (!hasCourseLink) return label || '-';
                          return (
                            <a
                              href={`https://classroom.google.com/c/${result.courseId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#1565c0',
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                            >
                              {label}
                            </a>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>
                        {result.postType || '-'}
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          border: '1px solid',
                          ...(result.color === 'red' ? { backgroundColor: '#fee', color: '#c00', borderColor: '#fcc' } :
                              result.color === 'orange' ? { backgroundColor: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' } :
                              result.color === 'yellow' ? { backgroundColor: '#ffc', color: '#880', borderColor: '#ff8' } :
                              result.color === 'green' ? { backgroundColor: '#efe', color: '#080', borderColor: '#8f8' } :
                              result.color === 'blue' ? { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' } :
                              result.color === 'na' ? { backgroundColor: '#9ca3af', color: '#111827', borderColor: '#4b5563' } :
                              result.color === 'none' ? { backgroundColor: '#ffffff', color: '#111827', borderColor: '#111827' } :
                              { backgroundColor: '#f5f5f5', color: '#666', borderColor: '#ddd' })
                        }}>
                          {result.aiUsageColor}
                        </span>
                      </td>
                      <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                        {formatDate(result.date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            {showPagination && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                marginTop: '20px',
                gap: '15px'
              }}>
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: currentPage === 1 ? '#f0f0f0' : '#4285f4',
                    color: currentPage === 1 ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: '14px', color: '#555' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: currentPage === totalPages ? '#f0f0f0' : '#4285f4',
                    color: currentPage === totalPages ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        {!isSuperAdminUser && <FeedbackFab />}
        {pdfExportOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15,23,42,0.42)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px'
            }}
          >
            <div style={{ width: '100%', maxWidth: '460px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #d1d5db', padding: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#111827' }}>Export Summary to PDF</h3>
              <p style={{ marginTop: '8px', marginBottom: '12px', fontSize: '13px', color: '#4b5563' }}>
                Optional title and subtitle will be shown above your dashboard summary in the exported PDF.
              </p>
              <div style={{ display: 'grid', gap: '10px' }}>
                <input
                  value={pdfTitle}
                  onChange={(e) => setPdfTitle(e.target.value)}
                  placeholder="Title (optional)"
                  style={{ padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                />
                <input
                  value={pdfSubtitle}
                  onChange={(e) => setPdfSubtitle(e.target.value)}
                  placeholder="Subtitle (optional)"
                  style={{ padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setPdfExportOpen(false)}
                  disabled={exportingPdf}
                  style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleExportSummaryPdf()}
                  disabled={exportingPdf}
                  style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', backgroundColor: exportingPdf ? '#9ca3af' : '#4285f4', color: 'white', fontWeight: 700, cursor: exportingPdf ? 'not-allowed' : 'pointer' }}
                >
                  {exportingPdf ? 'Exporting...' : 'Export'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
